import { ChangedPlanRecord, Spot } from "../../types"
import { FieldsKey, MediaFields } from "./types"
import { MediaField, MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import { getMediaFieldValue, roundHalfEven } from "./utils"
import { useAtomCallback, useUpdateAtom } from "jotai/utils"

import { BillingType } from "routes/Base/Admin/MarketSettings/List/BillingTypes/types"
import { CreatedTreeState } from "components/TreeState"
import { atom } from "jotai"
import { get } from "lodash/fp"
import { set } from "lodash"
import { usePrecision } from "providers/A12n"

type Aggregations = Record<string, Record<string, number>>

const aggregations = {}
export const tableAggregations = atom<Aggregations>(aggregations)

const AGGREGATED_FIELD_SEMANTICS: string[] = [
  "fees",
  "taxes",
  "netCost",
  "billable",
  "grossCost",
  "mediaCommissions",
  "agencyCommissions",
  "passthroughCosts",
  "netCostMarkup",
  "passthroughCostsMarkup",
]

export function useAggregations(
  fieldsKey: FieldsKey = "planningMediaFields",
  tableState: CreatedTreeState<ChangedPlanRecord>,
  mediaConfig?: MediaType[],
) {
  const update = useUpdateAtom(tableAggregations)
  const precision = usePrecision()

  const aggregate = useAtomCallback((get, _, li: ChangedPlanRecord) => {
    const tree = tableState.tree
    const rows = tableState.state

    if (mediaConfig) {
      const parentId = li.parentId
      // for the parent media get all children and summarize the fields
      if (parentId) {
        const parent = tree[parentId]
        if (!!parent) {
          const children = parent.children
          if (children?.length) {
            let aggregatedParents: Aggregations = {}
            let aggregatedAncestors: Aggregations = {}

            aggregatedParents = children.reduce((ag, childKey) => {
              const row = rows[childKey] as ChangedPlanRecord
              const childFields = getAggregationFields(
                row,
                fieldsKey,
                mediaConfig,
              )

              Object.keys(childFields)?.forEach((fkey) => {
                const existing = ag[parentId]?.[fkey] ?? 0
                const acc = roundHalfEven(
                  existing + childFields[fkey],
                  precision,
                )
                set(ag, [parentId, fkey], acc)
              })

              return ag
            }, {} as Aggregations)

            // update ancestor as well using the aggregated values
            const ancestorId = parent.parentId
            if (!!ancestorId) {
              const ancestor = tree[ancestorId]
              if (!!ancestor) {
                const ancestorRow = rows[ancestorId]
                const mediaType = mediaConfig.find(
                  (mt) => mt.id === ancestorRow?.mediaId,
                )
                const mediaFields = mediaType?.mediaFields?.filter((mf) =>
                  AGGREGATED_FIELD_SEMANTICS.includes(mf.semanticCode),
                )
                const children = ancestor.children

                if (children?.length && mediaFields?.length) {
                  const aggregations = get(tableAggregations)
                  const latest = { ...aggregations, ...aggregatedParents }
                  aggregatedAncestors = children.reduce((ag, childKey) => {
                    // aggregations persisted to table state
                    const persistedFields = getAggregationFields(
                      rows[childKey] as ChangedPlanRecord,
                      fieldsKey,
                      mediaConfig,
                    )

                    mediaFields.forEach((mf) => {
                      const existing = ag[ancestorId]?.[mf.id] ?? 0
                      const aggregated = latest[childKey]?.[mf.id]
                      const persisted = persistedFields?.[mf.id] ?? 0
                      const acc = roundHalfEven(
                        existing + (aggregated ?? persisted),
                        precision,
                      )
                      set(ag, [ancestorId, mf.id], acc)
                    })

                    return ag
                  }, {} as Aggregations)
                }
              }
            }

            update((v) => {
              return {
                ...v,
                ...aggregatedParents,
                ...aggregatedAncestors,
              }
            })
            /*
              handle rows where the aggregations need to be nullified
              i.e. rows (parent or ancestors) without line items
            */
          } else {
            const rows = tableState.state
            const parent = parentId
              ? (rows[parentId] as ChangedPlanRecord)
              : undefined

            update((v) => {
              return {
                ...v,
                [parentId]: {},
              }
            })

            const ancestorId = parent?.parentId
            if (ancestorId) {
              const ancestorNode = tree[ancestorId] ?? {}
              const children = ancestorNode.children
              const hasLineItems = !!children?.filter(
                (child) => rows[child]?.type === "line_item",
              )?.length

              if (!hasLineItems) {
                update((v) => {
                  return {
                    ...v,
                    [ancestorId]: {},
                  }
                })
              }
            }
          }
        }
      }
    }
  })

  return { aggregate }
}

function getAggregationFields(
  record: ChangedPlanRecord,
  fieldsKey: FieldsKey,
  mediaConfig: MediaType[],
): Record<string, number> {
  const mediaType = mediaConfig.find((mt) => mt.id === record.ancestorMediaId)
  if (!mediaType) return {}

  const mediaFields = mediaType.mediaFields?.filter((mf) =>
    AGGREGATED_FIELD_SEMANTICS.includes(mf.semanticCode),
  )
  const fields = record[fieldsKey]

  if (!mediaFields?.length || !fields) return {}

  const { billingType: billingTypeId } =
    mediaType.defaultFieldIdBySemantic || {}
  const btField = mediaType?.mediaFields?.find((mf) => mf.id === billingTypeId)

  const bt = !!btField
    ? getMediaFieldValue<BillingType["semanticCode"]>(fields, btField)
    : undefined

  const aggregationFields = mediaFields.reduce((af, mediaField) => {
    af = { ...af, [mediaField.id]: getMediaFieldAmount(fields, mediaField, bt) }

    return af
  }, {})

  return aggregationFields
}

export function getMediaFieldAmount(
  fields: MediaFields,
  field?: MediaField,
  billingType?: BillingType["semanticCode"],
) {
  if (!fields || !field) return 0

  let value: number

  switch (field.type) {
    case "NumericWithUnit":
      value = Number(get([field.id, "value"], fields))
      break
    case "VariableCost":
      value = Number(get([field.id, "defaultCost"], fields) ?? 0)
      break
    case "SpotsPlan":
      {
        const spots = get([field.id, "spots"], fields) as Array<Spot>
        value =
          spots?.reduce((sum, spot) => {
            sum += spot.quantity

            return sum
          }, 0) || 0
      }
      break
    case "Commission":
    case "Fees":
      value =
        billingType !== "bonus"
          ? Number(get([field.id, "value"], fields) ?? 0)
          : 0
      break
    case "Taxes":
      value = Number(get([field.id, "total"], fields) ?? 0)
      break
    default:
      value = Number(get([field.id], fields))
      break
  }

  return !isNaN(value) ? value : 0
}
